/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import PropTypes from 'prop-types';
import Switch from 'react-switch';
import _ from 'lodash';
import { Button, Typography, Checkbox } from '@telus-uds/components-web';

import ErrorIcon from '../../assets/images/icon_error_messages.gif';
import validationJSON from './schema';
import checkFieldToDisplay from './getPayloadKeys';
import { Wrapper } from './css';
import FormField from '../design/shared/form/formField';
import request from '../utils/request';
import { channelOutletMapping, CONSTANT ,storeCodes, outletIdStoreCodeMapping, storeCodeOutletMapping} from '../utils/constant';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import Spinner from './spinner';
import ErrorMessage from './error';
import { displayLabel } from './utils';
import { sortedObjects } from '../utils/utils';
import AccessControl from './accessControl';
import { useLocaleContext } from '../context/LocaleContext';
import EN from '../../EN.json';
import FR from '../../FR.json';

const propChannel = {
  id: PropTypes.number,
  name: PropTypes.string,
  description: PropTypes.string
};

const propProvince = {
  id: PropTypes.number,
  name: PropTypes.string
};

const propRole = {
  description: PropTypes.string,
  id: PropTypes.number,
  name: PropTypes.string
};

const propDealership = {
  id: PropTypes.number,
  name: PropTypes.string
};

const UserForm = ({
  user,
  roles,
  channels,
  provinces,
  dealershipList,
  submissionHandling
}) => {
  const locale = useLocaleContext();
  const localizedStrings = useLocalizedStringsContext();
  const TRANSLATION = localizedStrings.USERS.FORM;
  const {
    KEYS: {
      ROLES: { SALES_REP, SALES_SUPPORT, SALES_MANAGER },
      CHANNELS: { DEALER }
    }
  } = CONSTANT;
  const {
    USERS: {
      FORM: { MESSAGE }
    },
    USERS: {
      FORM: { LABEL }
    }
  } = locale === 'en-US' ? EN : FR;

  const _localJson = locale === 'en-US' ? EN : FR;
  const REQUIRES_ORDER_ACCESS_LABEL =
    _localJson.USERS.USER_AUTHENTICATION.REGISTRATION_SCHEMA_MESSAGES.REQUIRES_ORDER_ACCESS_LABEL;
  const SELECT_OPTION_LABEL =
    _localJson.USERS.USER_AUTHENTICATION.labels.SELECT_OPTION;

  const [formsControl, setFormsControl] = useState({});
  const [loading, setLoading] = useState(false);
  const [isError, setError] = useState(null);
  const {
    id,
    role: roleObj,
    firstName,
    lastName,
    email,
    contactNumber,
    provinces: userProvinces,
    salesManager,
    salesManagerEmail,
    salesRepId,
    requiresOrderAccess,
    dealershipVisibilityEnabled,
    channels: userChannels,
    outlet,
    dealership,
    channelOrgCode,
    xid,
    storeCode,
    formTypes,
    isAuthDisabled
  } = user;

  const channelId =
    userChannels &&
    userChannels.length &&
    userChannels.map(({ name, description }) => {
      return { id: name, label: description };
    });

  const provinceId =
    userProvinces && userProvinces.length && userProvinces[0].name;
  const channelsIds = userChannels.map(item => item.name);
  const provincesIds = userProvinces.map(item => item.name);
  const sortedProvinces = sortedObjects(provinces, 'name').map(row => ({
    ...row,
    id: row.name
  }));
  const sortedChannels = sortedObjects(channels, 'description').map(row => ({
    ...row,
    id: row.name
  }));
  const sortedRoles = sortedObjects(roles, 'description').map(row => ({
    ...row,
    id: row.name
  }));
  const getManagersLabel = (labelA, labelB) => {
    return roleObj.id === 2 || roleObj.id === 12 || roleObj.id === 13
      ? labelA
      : labelB;
  };
  const formFieldList = [
    {
      label: displayLabel(TRANSLATION.LABEL.ROLE),
      type: 'select',
      name: 'roleId',
      id: 'role-id',
      defaultVal: roleObj.name,
      list: sortedRoles,
      allowOptionNull: false,
      val: 'description'
    },
    {
      label: displayLabel(TRANSLATION.LABEL.FIRST_NAME),
      type: 'text',
      name: 'firstName',
      placeholder: TRANSLATION.LABEL.FIRST_NAME
    },
    {
      label: displayLabel(TRANSLATION.LABEL.LAST_NAME),
      type: 'text',
      name: 'lastName',
      placeholder: TRANSLATION.LABEL.LAST_NAME
    },
    {
      label: displayLabel(TRANSLATION.LABEL.EMAIL),
      type: 'email',
      name: 'email',
      placeholder: TRANSLATION.LABEL.EMAIL,
      readOnly: true
    },
    {
      label: displayLabel(TRANSLATION.LABEL.CONTACT_NUMBER),
      type: 'text',
      name: 'contactNumber',
      id: 'role-id',
      placeholder: TRANSLATION.LABEL.CONTACT_NUMBER
    },
    {
      label: displayLabel(TRANSLATION.LABEL.PROVINCE),
      type: 'select',
      name: 'province',
      id: 'province-id',
      defaultVal: provinceId,
      list: sortedProvinces,
      val: 'name'
    },
    {
      label: displayLabel(TRANSLATION.LABEL.PROVINCE),
      type: 'multiCheckbox',
      name: 'provinceIds',
      id: 'province-ids',
      defaultVal: provincesIds,
      list: sortedProvinces,
      val: 'name'
    },
    {
      label: displayLabel(LABEL.CHANNEL),
      type: 'channelField',
      name: 'channelId',
      id: 'channel-id',
      defaultVal: channelId,
      list: sortedChannels,
      val: 'description'
    },
    {
      label: displayLabel(LABEL.CHANNEL),
      type: 'multiCheckbox',
      name: 'channelIds',
      id: 'channel-ids',
      defaultVal: channelsIds,
      list: sortedChannels,
      val: 'description'
    },
    {
      label: displayLabel(TRANSLATION.LABEL.DEALERSHIP),
      type: 'dealershipField',
      name: 'dealership',
      list: dealershipList
    },
    {
      label: displayLabel(TRANSLATION.LABEL.CHANNEL_ORGANIZATION_CODE),
      type: 'text',
      name: 'channelOrgCode',
      placeholder: TRANSLATION.LABEL.CHANNEL_ORGANIZATION_CODE
    },
    {
      label: displayLabel(TRANSLATION.LABEL.STORE_CODE),
      type: 'corpStoreField',
      name: 'storeCode',
      placeholder: TRANSLATION.LABEL.STORE_CODE,
      list:storeCodes

    },
    {
      label: displayLabel(TRANSLATION.LABEL.XID),
      type: 'text',
      name: 'xid',
      placeholder: TRANSLATION.LABEL.XID,
      note: 'Note: Enter your full TELUS ID starting with X or T (e.g., T123456)'
    },
    {
      label: displayLabel(TRANSLATION.LABEL.CPMS_ID),
      type: 'text',
      name: 'cpmsId',
      placeholder: TRANSLATION.LABEL.CPMS_ID
    },
    {
      label: displayLabel(TRANSLATION.LABEL.OUTLET),
      type: 'text',
      name: 'outlet',
      placeholder: TRANSLATION.LABEL.OUTLET
    },
    {
      label: displayLabel(
        getManagersLabel(
          TRANSLATION.LABEL.SALE_MANAGER,
          TRANSLATION.LABEL.MANAGER
        )
      ),
      type: 'text',
      name: 'salesManager',
      placeholder: getManagersLabel(
        TRANSLATION.LABEL.SALE_MANAGER,
        TRANSLATION.LABEL.MANAGER
      )
    },
    {
      label: displayLabel(
        getManagersLabel(
          TRANSLATION.LABEL.SALE_MANAGER_EMAIL,
          TRANSLATION.LABEL.MANAGER_EMAIL
        )
      ),
      type: 'text',
      name: 'salesManagerEmail',
      placeholder: getManagersLabel(
        TRANSLATION.LABEL.SALE_MANAGER_EMAIL,
        TRANSLATION.LABEL.MANAGER_EMAIL
      )
    }
  ];

  const getDealershipInitialState = () => {
    const selectedDealershipObj = dealershipList.filter(
      row => row.id === +dealership
    );
    const transformedDealershipObj = selectedDealershipObj.map(row => ({
      id: row.id,
      label: row.name
    }));
    return transformedDealershipObj;
  };

  const initialState = {
    id,
    role: roleObj.name,
    email,
    firstName,
    lastName,
    contactNumber: contactNumber || '',
    province: provinceId || '',
    provinceIds: provincesIds,
    salesManager: salesManager || '',
    salesManagerEmail: salesManagerEmail || '',
    cpmsId: salesRepId || '',
    requiresOrderAccess:
      requiresOrderAccess ||
      (roleObj.name === SALES_MANAGER && channelsIds.includes(DEALER)
        ? 'no'
        : ''),
    dealershipVisibilityEnabled: dealershipVisibilityEnabled || false,
    channelId: channelId || '',
    channelIds: channelsIds,
    outlet: outlet || '',
    dealership: getDealershipInitialState(),
    roleId: roleObj.name || '',
    channelOrgCode: channelOrgCode || '',
    xid: xid || '',
    storeCode: storeCode || '',
    isAuthDisabled: isAuthDisabled || false
  };

  const handleSubmit = async values => {
    const payloadKeys = checkFieldToDisplay(values.roleId, values);
    const payload = Object.keys(values)
      .filter(key => key === 'isAuthDisabled' || payloadKeys.includes(key))
      .reduce((obj, key) => {
        let newKey = key;
        let value = values[key];

        if (key === 'roleId') newKey = 'role';
        if (['channelId', 'channelIds'].includes(key)) newKey = 'channels';
        else if (['provinceIds', 'province'].includes(key))
          newKey = 'provinces';

        if (key === 'province')
          value =
            value.roleId === SALES_SUPPORT
              ? values.provinceIds
              : [values.province];
        if (key === 'channelId')
          value =
            value.roleId === SALES_SUPPORT
              ? values.channelIds
              : [_.get(values, 'channelId.0.id')];
        if (key === 'dealership') value = _.get(value, '0.label');

        return {
          ...obj,
          [newKey]: value
        };
      }, {});

    // FR-018: SM+Dealer without order access should not submit cpmsId
    if (
      values.roleId === SALES_MANAGER &&
      _.get(values, 'channelId.0.id') === DEALER &&
      values.requiresOrderAccess !== 'yes'
    ) {
      delete payload.cpmsId;
    }

    setError(null);
    setLoading(true);

    try {
      const resultUser = await request.post(
        `/users/${values.id}/update`,
        payload
      );
      if (resultUser && resultUser.status === 200) {
        const resultFormControls = await request.put(
          '/disable-form-access-for-user-or-outlet',
          {
            email: payload.email,
            forms: { ...formsControl }
          }
        );

        if (resultFormControls && resultFormControls.status === 200) {
          setLoading(false);
        }
      }
    } catch (err) {
      const {
        data: { error }
      } = err && err.response;
      setLoading(false);
      setError(error);
    } finally {
      submissionHandling();
    }
  };

  const getFieldLabel = (role, { label, name }) => {
    if (['outlet', 'cpmsId'].includes(name) && role === SALES_SUPPORT) {
      return label.replace('*', '');
    }

    if (name === 'salesManager' && role === SALES_REP) {
      return `${TRANSLATION.LABEL.SALE_MANAGER}*`;
    }

    if (name === 'salesManagerEmail' && role === SALES_REP) {
      return `${TRANSLATION.LABEL.SALE_MANAGER_EMAIL}*`;
    }
    return label;
  };

  const getChannel = outletValue => {
    let result = '';
    Object.keys(channelOutletMapping).forEach(channel => {
      if (_.get(channelOutletMapping, channel, []).includes(outletValue))
        result = channels.filter(ch => ch.name === channel);
    });
    return (
      result &&
      result.map(({ name, description }) => {
        return { id: name, label: description };
      })
    );
  };
    const getMappedStoreCode = outletId => {
      return outletIdStoreCodeMapping[outletId] || null;
  };
  const getMappedOutletId = storeCodeValue => {
    return storeCodeOutletMapping[storeCodeValue] || null;
    };

  useEffect(() => {
    if (formTypes && Object.keys(formTypes).length > 0)
      setFormsControl(formTypes);
  }, []);

  return (
    <Wrapper className="user-profile-form">
      {loading ? (
        <Spinner />
      ) : (
        <React.Fragment>
          <div id="main-content">
            <Typography variant={{ bold: true }}>
              {MESSAGE.FILL_DETAILS}
            </Typography>
          </div>
          {isError && (
            <ErrorMessage message={MESSAGE.SERVER_ERROR} isError={isError} />
          )}
          <div id="main-content-form">
            <Formik
              initialValues={initialState}
              validationSchema={validationJSON}
              onSubmit={values => {
                handleSubmit(values);
              }}
            >
              {({
                errors,
                touched,
                values,
                setFieldValue,
                setFieldTouched,
                submitForm
              }) => {
                return (
                  <Form>
                    <div
                      style={{
                        padding: '1rem 0 2rem 0',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem'
                      }}
                    >
                      <Typography bold>Two Factor Authentication</Typography>
                      <Switch
                        onColor="#4B286D"
                        onChange={() =>
                          setFieldValue(
                            'isAuthDisabled',
                            !values.isAuthDisabled
                          )
                        }
                        checked={!values.isAuthDisabled}
                      />
                    </div>

                    <Field name="id" type="hidden" />
                    {useEffect(() => {
                      const showRequiresOrderAccess =
                        values.roleId === SALES_MANAGER &&
                        _.get(values, 'channelId.0.id') === DEALER;
                      /* eslint-disable no-param-reassign */
                      if (!showRequiresOrderAccess) {
                        values.requiresOrderAccess = '';
                      } else if (!values.requiresOrderAccess) {
                        values.requiresOrderAccess = 'no';
                      }
                      /* eslint-enable no-param-reassign */
                    }, [values.roleId, values.channelId])}
                    {formFieldList.map((field, index) => {
                      const key = `formField${index}`;
                      const isSMDealer = values.roleId === SALES_MANAGER && _.get(values, 'channelId.0.id') === DEALER;
                      if (field.name === 'cpmsId' && isSMDealer) return null;
                      return checkFieldToDisplay(values.roleId, values).map(
                        fieldName => {
                          const fieldData = { ...field };
                          fieldData.label = getFieldLabel(
                            values.roleId,
                            fieldData
                          );

                          if (!(field.name === fieldName)) return null;
                          const fieldRender = (
                            <FormField
                              key={key}
                              field={fieldData}
                              errors={errors}
                              touched={touched}
                              errorIcon={ErrorIcon}
                              channels={channels}
                              values={values}
                              setFieldValue={data => {
                                let value;
                                if (
                                  ['multicheckbox', 'typehead'].includes(
                                    field.type
                                  )
                                ) {
                                  value = data.length > 0 ? data[0].id : '';

                                  if (
                                    ['channelIds', 'provinceIds'].includes(
                                      field.name
                                    )
                                  )
                                    value = data;
                                } else value = data;
                                if( field.type === 'corpStoreField' && field.name === 'storeCode' )
                                {
                                    setFieldValue(field.name,value,true);
                                    const mappedOutlet = getMappedOutletId (value);
                                    if (mappedOutlet) {
                                        setFieldValue('outlet', mappedOutlet);
                                    }
                                }

                                setFieldValue(field.name, value, true);
                                if (
                                  field.name === 'outlet' &&
                                  getChannel(value)
                                )
                                  setFieldValue('channelId', getChannel(value));
                                  const mappedStoreCode = getMappedStoreCode(value);
                                if(mappedStoreCode)
                                    setFieldValue('storeCode',mappedStoreCode);
                              }}
                              setFieldTouched={() =>
                                setFieldTouched(field.name, true)
                              }
                            />
                          );
                          if (field.name === 'channelOrgCode' && isSMDealer) {
                            return (
                              <React.Fragment key={`sm-dealer-block-${key}`}>
                                {fieldRender}
                                <div className="main-content-form-headers">
                                  <label className="frg-label">{REQUIRES_ORDER_ACCESS_LABEL}</label>
                                </div>
                                <div
                                  style={{ position: 'relative', width: '100%' }}
                                  className={`frg-select-container color-light input-container ${
                                    errors.requiresOrderAccess && touched.requiresOrderAccess ? 'error' : ''
                                  }`}
                                >
                                  <Field as="select" name="requiresOrderAccess" className="select-style">
                                    <option value="">{SELECT_OPTION_LABEL}</option>
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                  </Field>
                                  <div className="error_messages">
                                    <span className="registration-error">{errors.requiresOrderAccess}</span>
                                  </div>
                                  <div id="username_error_icon" className="error_messages_icon">
                                    <img src={ErrorIcon} alt="error_icon" />
                                  </div>
                                </div>
                                {values.requiresOrderAccess === 'yes' && (
                                  <>
                                    <div className="main-content-form-headers">
                                      <label className="frg-label">{TRANSLATION.LABEL.CPMS_ID}</label>
                                    </div>
                                    <div
                                      style={{ position: 'relative' }}
                                      className={`input-container ${errors.cpmsId && touched.cpmsId ? 'error' : ''}`}
                                    >
                                      <Field
                                        type="text"
                                        name="cpmsId"
                                        className="select-style"
                                        placeholder={TRANSLATION.LABEL.CPMS_ID}
                                      />
                                      <div className="error_messages">
                                        <span className="registration-error">{errors.cpmsId}</span>
                                      </div>
                                      <div id="username_error_icon" className="error_messages_icon">
                                        <img src={ErrorIcon} alt="error_icon" />
                                      </div>
                                    </div>
                                  </>
                                )}
                              </React.Fragment>
                            );
                          }
                          return fieldRender;
                        }
                      );
                    })}
                    {request.getProfile() &&
                      request.getProfile().role &&
                      request.getProfile().role.name === CONSTANT.KEYS.ROLES.ADMIN &&
                      values.roleId === SALES_MANAGER &&
                      _.get(values, 'channelId.0.id') === DEALER && (
                        <div style={{ margin: '1rem 0' }}>
                          <Checkbox
                            id="dealership-visibility"
                            name="dealershipVisibilityEnabled"
                            label={LABEL.DEALERSHIP_VISIBILITY_LABEL}
                            checked={values.dealershipVisibilityEnabled}
                            onChange={() =>
                              setFieldValue(
                                'dealershipVisibilityEnabled',
                                !values.dealershipVisibilityEnabled
                              )
                            }
                          />
                        </div>
                      )}
                    {values.role.id !== 1 && (
                      <AccessControl
                        formsControl={formsControl}
                        setFormsControl={setFormsControl}
                      />
                    )}
                    <div id="submit_button" style={{ marginTop: 20 }}>
                      {!loading && (
                        <span>
                          <Button
                            type="submit"
                            onPress={submitForm}
                            variant={{ priority: 'high' }}
                            style={{ width: '100%' }}
                          >
                            {TRANSLATION.BUTTON.SUBMIT}
                          </Button>
                        </span>
                      )}
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </React.Fragment>
      )}
    </Wrapper>
  );
};

UserForm.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    provinces: PropTypes.arrayOf(PropTypes.shape(propProvince)),
    status: PropTypes.string,
    contactNumber: PropTypes.string,
    outlet: PropTypes.string,
    salesRepId: PropTypes.string,
    channels: PropTypes.arrayOf(PropTypes.shape(propChannel)),
    role_id: PropTypes.number,
    cpms_id: PropTypes.string,
    role: PropTypes.shape(propRole),
    salesManager: PropTypes.string,
    salesManagerEmail: PropTypes.string,
    managerName: PropTypes.string,
    managerEmail: PropTypes.string,
    dealership: PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string
    }),
    channelOrgCode: PropTypes.string,
    xid: PropTypes.string,
    storeCode: PropTypes.string,
    formTypes: PropTypes.shape({}),
    isAuthDisabled: PropTypes.bool
  }).isRequired,
  roles: PropTypes.arrayOf(PropTypes.shape(propRole)).isRequired,
  channels: PropTypes.arrayOf(PropTypes.shape(propChannel)).isRequired,
  provinces: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dealershipList: PropTypes.arrayOf(PropTypes.shape(propDealership)).isRequired,
  submissionHandling: PropTypes.func.isRequired
};

export default UserForm;
